// /login — sign in, create account, password reset.
//   ?mode=signup   open on Create account
//   ?next=/path    where to go afterwards (same-site paths only)
//   ?reset=1       landing from the password-reset email: set a new password
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Globe2, Headset, Loader, Lock, Sparkles } from 'lucide-react';
import AuthComponent from '../components/Auth';
import { supabase } from '../config/supabase';
import { routeAfterSignIn, safeNext } from '../services/onboarding';
import './LoginPage.css';

const STRIP = ['real-black-woman', 'glam-doll', 'afrofuturist', 'anime', 'streetwear', 'fantasy', 'cyberpunk', 'chibi', 'toon-3d', 'real-asian-man', 'low-poly', 'real-latina-woman'];

const NewPassword: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(false);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw.length < 8) return setErr('Use at least 8 characters.');
    setBusy(true); setErr('');
    const { error } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (error) return setErr(error.message);
    setOk(true);
    window.setTimeout(onDone, 900);
  };
  return (
    <div className="gxa">
      <div className="gxa-head"><h2>Set a new password</h2><p>Choose something you haven't used here before.</p></div>
      <form className="gxa-form" onSubmit={save}>
        <label className="gxa-field">
          <span className="gxa-label">New password</span>
          <span className="gxa-input"><Lock size={17} aria-hidden /><input type="password" autoComplete="new-password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" /></span>
        </label>
        {err && <div className="gxa-alert" role="alert">{err}</div>}
        {ok && <div className="gxa-alert is-ok" role="status"><Check size={16} /> Password updated.</div>}
        <button className="gxa-submit" disabled={busy}>{busy && <Loader size={17} className="spin" />} Save password</button>
      </form>
    </div>
  );
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [recovery, setRecovery] = useState(params.get('reset') === '1');
  const [checked, setChecked] = useState(false);

  const proceed = useCallback(async () => {
    navigate(await routeAfterSignIn(next), { replace: true });
  }, [navigate, next]);

  // Already signed in? Skip the form (except when resetting a password).
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setRecovery(true);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && params.get('reset') !== '1') void proceed();
      else setChecked(true);
    });
    return () => subscription.unsubscribe();
  }, [proceed, params]);

  return (
    <div className="gx-login">
      <video className="gx-login-bg" autoPlay muted loop playsInline poster="/auth/auth-poster.webp" aria-hidden>
        <source src="/auth/auth-loop.webm" type="video/webm" />
        <source src="/auth/auth-loop.mp4" type="video/mp4" />
      </video>
      <div className="gx-login-shade" aria-hidden />

      <header className="gx-login-top">
        <a href="/" className="gx-lockup gx-login-lockup" aria-label="GathrXR home">
          <img src="/brand/gathrxr-mark.png" alt="" />
          <span className="gx-word">Gathr<span>XR</span></span>
        </a>
      </header>

      <main className="gx-login-main">
        <section className="gx-login-pitch">
          <h1>Your worlds are <em>waiting.</em></h1>
          <p>Build 3D spaces in minutes, show up as the avatar you design, and hang out together — on your phone, computer or headset.</p>
          <ul className="gx-login-points">
            <li><Globe2 size={16} /> Build and share worlds in your browser</li>
            <li><Sparkles size={16} /> Start with a free robot or the free Avatar Builder, or design your own</li>
            <li><Headset size={16} /> Step inside with VR, hands-free</li>
          </ul>
          <div className="gx-login-strip" aria-hidden>
            <div className="gx-login-strip-track">
              {[...STRIP, ...STRIP].map((id, i) => <img key={i} src={`/avatar-styles/${id}.webp`} alt="" loading="lazy" />)}
            </div>
          </div>
        </section>

        <section className="gx-login-card">
          {!checked ? (
            <div className="gx-login-wait"><Loader size={22} className="spin" /></div>
          ) : recovery ? (
            <NewPassword onDone={() => { setRecovery(false); void proceed(); }} />
          ) : (
            <AuthComponent
              initialMode={params.get('mode') === 'signup' ? 'signup' : 'signin'}
              next={next}
              onSuccess={proceed}
            />
          )}
        </section>
      </main>
    </div>
  );
};

export default LoginPage;
